import { onDeleteRecurringDialogCloseThunk } from "@common-reducers/CalendarThunks";
import { editCalendarEventThunk } from "@common-reducers/CalendarThunks";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { getCalendarEventById } from "@common/reducers/CalendarSelectors";
import { confirmationDialogOptions } from "@common-views/calendar/calendar-view";
import { deleteUserMessage } from "@common/thunks/UserMessagesThunks";
import { MessageConfirmationDialog } from "@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog";
import { Pencil, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import {
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

type Props = {
    message: any; // This is a lean event from calendar
    onMenuClose?: () => void;
    // Kept for call-site compatibility. It no longer influences the delete
    // scope: the surface a delete is opened from must not be able to widen it.
    fromCalendarSidebar?: boolean;
}

export default function MessageRowMenu({ message: leanMessage, onMenuClose }: Props) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const [showConfirmation, setShowConfirmation] = useState(false);
    
    // Get full message data using the same selector as custom-message-event.tsx
    const message = useAppSelector(state => 
        getCalendarEventById(state, leanMessage._id, leanMessage.dueDate, leanMessage.isCampaign)
    );

    // Close menu when message is deleted
    useEffect(() => {
        if (!message && onMenuClose) {
            onMenuClose();
        }
    }, [message, onMenuClose]);

    // If message was deleted (undefined), return null
    if (!message) {
        return null;
    }

    const handleEditClick = () => {
        // Use the bridging thunk to fetch full UserMessage data and open edit dialog
        // Use leanMessage (the original lean event) for editCalendarEventThunk
        dispatch(editCalendarEventThunk(leanMessage));
        if (onMenuClose) {
            onMenuClose();
        }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowConfirmation(true);
        if (onMenuClose) {
            onMenuClose();
        }
    };

    // Use the exact same logic as DeleteMessageButton
    const handleConfirmationClose = async (confirmed?: string) => {
        // If any value is passed (even empty string), it means Ok was clicked
        // If undefined is passed, it means Cancel was clicked
        if (confirmed !== undefined) {
            // Store message data before deletion (message may become undefined after deletion)
            const messageToDelete = message || leanMessage;
            
            // Handle recurring messages - use proper recurring logic
            if (messageToDelete.isRecurring && messageToDelete.rruleset) {
                // The scope is exactly what the user picked — on EVERY surface.
                // The calendar sidebar used to discard the selection and force
                // "All messages", so the same red Delete silently destroyed the
                // whole series. The sidebar row knows its own occurrence date
                // (messageToDelete.dueDate), so it can ask the same question.
                const scope: string = confirmed;

                await dispatch(onDeleteRecurringDialogCloseThunk({
                    value: scope as any, 
                    event: {
                        _id: messageToDelete._id,
                        dueDate: messageToDelete.dueDate,
                        isCampaign: false,
                        isRecurring: true
                    }
                })).unwrap();
            }
            // Handle regular messages
            else {
                if (messageToDelete._id) {
                    await dispatch(deleteUserMessage(messageToDelete._id)).unwrap();
                }
            }
        }
        setShowConfirmation(false);
    };

    // Offer the scope choice for EVERY recurring message, on every surface —
    // including the calendar sidebar, which used to suppress the question and
    // wipe the series. Same rule as DeleteMessageButton (#149).
    const isRecurring = Boolean(message?.isRecurring && message?.rruleset);
    const shouldShowOptions = isRecurring;

    const dialogTitle = isRecurring
        ? t('scheduledMessage.confirmation.deleteRecurringTitle')
        : t('scheduledMessage.confirmation.deleteTitle');
    // The consequence must be visible next to the choice: the "whole series"
    // option destroys every future occurrence with no second step.
    const dialogDescription = isRecurring
        ? t('scheduledMessage.confirmation.deleteScopePrompt')
        : undefined; // Use default description for regular messages

    return (
        <>
            <DropdownMenuContent 
                className="p-1 min-w-[245px] z-[10000] bg-white dark:bg-slate-800"
                align="end"
                sideOffset={4}
                style={{ 
                    zIndex: 10000,
                    backgroundColor: 'var(--color-surface, #ffffff)',
                    color: 'var(--color-text, #1a202c)',
                    borderColor: 'var(--color-border, #e5e7eb)',
                }}
            >
                <DropdownMenuItem
                    onClick={handleEditClick}
                    className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)]"
                >
                    <Pencil className="h-4 w-4" />
                    <span className="ml-1.25">{t('common.edit')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={handleDeleteClick}
                    className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)] text-red-600 dark:text-red-400"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="ml-1.25">{t('common.delete')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>

            {/* Use the exact same dialog as DeleteMessageButton */}
            {showConfirmation && (
                <MessageConfirmationDialog
                    title={dialogTitle}
                    description={dialogDescription}
                    onClose={handleConfirmationClose}
                    shouldShowCancelButton={true}
                    destructive={true}
                    options={shouldShowOptions ? confirmationDialogOptions : undefined}
                />
            )}
        </>
    );
}

